/** 
 * Breeze SDK Integration - Consolidated & Optimized
 * Author: @seetam-cultsport
 * 
 * This module handles:
 * - Breeze/BlazeSDK initialization
 * - Checkout button click handlers
 * - Cart and PDP offers fetching and rendering
 * - Offer application and management
 */

(function() {
  'use strict';

  // ============================================================================
  // INITIALIZATION & GLOBAL STATE
  // ============================================================================

  window.BlazeSDK = window.BlazeSDK || {};
  window.breeze = window.breeze || {};

  window.allOffers = {
    pdpOffers: [],
    cartOffers: [],
    inEligibleOffers: {
      cart: [],
      pdpOffers: [],
    },
    cartData: {
      cartTotal: 0,
    },
  };

  // SDK State Management (Singleton Pattern)
  let blazeSDKInitialized = false;
  let blazeSDKInitPromise = null;
  let sdkInitStartTime = null;

  // Request Deduplication & Caching
  let pdpOffersRequestInProgress = false;
  let cartOffersRequestInProgress = false;
  let pdpOffersDebounceTimer = null;
  let pdpOffersAbortController = null;
  let cartOffersAbortController = null;
  const offerCache = new Map(); // Cache with TTL
  const CACHE_TTL = 30000; // 30 seconds

  // Performance Monitoring
  const performanceMetrics = {
    sdkLoadTime: 0,
    offerFetchTime: 0,
    renderTime: 0,
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Cache utilities with TTL
   */
  function getCachedOffer(key) {
    const cached = offerCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    offerCache.delete(key);
    return null;
  }

  function setCachedOffer(key, data) {
    offerCache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Get customer ID from localStorage
   */
  function getCustomerIdFromLocalStorage() {
    return localStorage.getItem("platformCustomerId") || null;
  }

  /**
   * Get cookie value by name
   */
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Wait for Breeze/BlazeSDK to be available
   * @param {number} totalMs - Maximum time to wait in milliseconds
   * @returns {Promise<boolean>} - True if SDK is available, false otherwise
   */
  async function waitForBreezeSDK(totalMs = 2000) {
    const start = Date.now();
    const intervals = [50, 100, 200, 400, 800, 400];
    for (let d of intervals) {
      if (typeof window.Breeze !== 'undefined' || 
          typeof window.breezeCheckout !== 'undefined' ||
          (window.BlazeSDK && typeof window.BlazeSDK.process === 'function')) {
        return true;
      }
      if (Date.now() - start >= totalMs) break;
      await new Promise(r => setTimeout(r, d));
    }
    return (typeof window.Breeze !== 'undefined' || 
            typeof window.breezeCheckout !== 'undefined' ||
            (window.BlazeSDK && typeof window.BlazeSDK.process === 'function'));
  }

  /**
   * Fetch customer session data from Breeze endpoint
   * @returns {Promise<Object|null>} - Session data or null
   */
  async function fetchCustomerSession() {
    try {
      const response = await fetch("/cart?view=breeze");
      const sessionData = await response.json();

      if (sessionData.customer) {
        return sessionData;
      }

      return null;
    } catch (error) {
      console.error("Error fetching customer session:", error);
      throw new Error("Unable to verify customer session");
    }
  }

  /**
   * Start Breeze checkout flow
   * @param {Object} checkoutPayload - The checkout payload
   * @param {boolean} isWebview - Whether running in webview
   * @param {boolean} isAndroid - Whether running on Android
   * @returns {boolean} - Success status
   */
  function startBreezeCheckout(checkoutPayload, isWebview = false, isAndroid = false) {
    try {
      const isApp = localStorage.getItem("isApp");
      
      if (
        isWebview &&
        isApp &&
        window.ReactNativeWebView &&
        typeof window.ReactNativeWebView.postMessage === "function"
      ) {
        // Native app checkout
        const nativePayload = {
          action: "blazeStartNativeCheckout",
          data: {
            ...checkoutPayload,
            payload: {
              ...checkoutPayload.payload,
              safeMargin: {
                top: isAndroid ? 40 : 0,
              },
            },
          },
        };

        // Get cart items for analytics
        const breezeItemsArray = localStorage.getItem("gaPxCartItems")
          ? JSON.parse(localStorage.getItem("gaPxCartItems"))
          : checkoutPayload.payload.cart.items.map(item => ({
              id: item.id,
              name: item.title,
              variant: item.variant_id,
              item_brand: item.vendor,
              quantity: item.quantity,
              price: item.price / 100,
              currency: "INR",
              vendor: item.vendor,
              productType: item.product_type,
            }));

        // Fire analytics events
        if (window.gtag) {
          window.gtag('event', 'begin_checkout', {
            send_to: "G-ZWHZKTYB0D",
            currency: "INR",
            value: checkoutPayload.payload.cart.total_price,
            items: breezeItemsArray,
            breeze_event_type: 'breeze_checkout_initiated',
            breeze_event_time: new Date().toISOString()
          });
        }

        if (window.fbq) {
          window.fbq(
            'track',
            "InitiateCheckout",
            {
              content_type: 'product_group',
              value: checkoutPayload.payload.cart.total_price / 100,
              content_ids: breezeItemsArray.map(item => item.id),
              contents: breezeItemsArray,
              num_items: breezeItemsArray.length,
              currency: "INR",
            },
            {
              eventID: `breeze_checkout_${Date.now()}`,
            }
          );
        }

        window.ReactNativeWebView.postMessage(JSON.stringify(nativePayload));
        console.log("Breeze native checkout initiated");
        return true;
      } else if (window.BlazeSDK && checkoutPayload) {
        // Web checkout
        window.BlazeSDK.process(checkoutPayload);
        console.log("Breeze web checkout initiated");
        return true;
      } else {
        const error = new Error("Blaze SDK unavailable or payload missing");
        console.error("Breeze checkout error:", error);
        
        // Report to Sentry if available
        if (window.CultStoreSentry) {
          window.CultStoreSentry.captureCheckoutError(error, {
            stage: 'sdk_launch',
            method: 'startBreezeCheckout',
            hasBlazeSDK: !!window.BlazeSDK,
            hasPayload: !!checkoutPayload,
            hasReactNativeWebView: !!window.ReactNativeWebView,
            isWebview: isWebview,
            userAgent: navigator.userAgent,
            pageUrl: window.location.href,
          });
        }
        
        throw error;
      }
    } catch (error) {
      console.error("Breeze checkout rendering error:", error);
      
      if (window.CultStoreSentry && !error.message.includes("unavailable or payload missing")) {
        window.CultStoreSentry.captureCheckoutError(error, {
          stage: 'sdk_launch_exception',
          method: 'startBreezeCheckout',
          errorMessage: error.message,
          errorStack: error.stack,
          pageUrl: window.location.href,
        });
      }
      
      throw error;
    }
  }

  /**
   * Generate unique request ID
   */
  window.breeze.generateShortUniqueId = (type = "cart") => {
    return `id-${type}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  /**
   * Format currency in Indian Rupees
   */
  function formatIndianRupees(amount) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount).replace("₹", "Rs.");
  }

  // ============================================================================
  // CHECKOUT BUTTON HANDLER
  // ============================================================================

  window.breeze.onCheckoutButtonClick = async (props) => {
    if (props !== null) {
      try {
        const decodedProps = atob(props);
        const jsonProps = JSON.parse(decodedProps);
        const cartItems = jsonProps !== null ? [jsonProps] : [];
        if (cartItems !== null && cartItems.length > 0) {
          return {
            checkoutFlag: true,
            offerCode: "nothing",
            platformCustomerId: getCustomerIdFromLocalStorage(),
          };
        }
      } catch (e) {
        console.error("Breeze| Exception| onCheckoutButtonClick: ", e);
      }
    }
    return {
      checkoutFlag: true,
      offerCode: "nothing",
      platformCustomerId: getCustomerIdFromLocalStorage(),
    };
  };

  // ============================================================================
  // CART ITEMS TRANSFORMATION
  // ============================================================================

  /**
   * Fetch and transform cart items to Breeze format
   */
  async function fetchCartItems(currentCartData = null) {
    try {
      let cartdata = {
        items: currentCartData || [],
      };
      
      if (!cartdata.items || cartdata.items.length === 0) {
        const response = await fetch(window.Shopify.routes.root + "cart.js");
        cartdata = await response.json();
      }

      return cartdata.items.map((item) => ({
        id: item.variant_id.toString(),
        type: item.product_type.replace(/\s+/g, "_"),
        quantity: item.quantity,
        price: item.line_price > 0 ? (item.price / 100).toString() : "0",
      }));
    } catch (error) {
      console.error("Error fetching cart items:", error);
      return [];
    }
  }

  // ============================================================================
  // OFFERS API INTEGRATION
  // ============================================================================

  /**
   * Fetch and render offers in cart with optimization
   */
  const fetchAndRenderOffersInCart = async (cartData = null) => {
    // Prevent duplicate requests
    if (cartOffersRequestInProgress) {
      console.log("Cart offers request already in progress, skipping...");
      return;
    }

    const cartItems = await fetchCartItems(cartData);
    if (cartItems.length === 0) return;

    const customerId = getCustomerIdFromLocalStorage();
    const cacheKey = `cart-${customerId}-${JSON.stringify(cartItems)}`;
    
    // Check cache first
    const cachedOffers = getCachedOffer(cacheKey);
    if (cachedOffers) {
      console.log("Using cached cart offers");
      window.breeze.offersCallback(cachedOffers);
      return;
    }

    cartOffersRequestInProgress = true;
    const startTime = performance.now();

    // Abort previous request if any
    if (cartOffersAbortController) {
      cartOffersAbortController.abort();
    }
    cartOffersAbortController = new AbortController();

    const payload = {
      keyId: "56263",
      action: "listOffers",
      customerData: { id: customerId },
      cart: JSON.stringify({ items: cartItems }),
    };

    try {
      const shopifyYCookie = getCookie("_shopify_y");
      const response = await fetch(
        "https://tesseract.cultstore.com/juspay/gen-offer-signature",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Device-ID": shopifyYCookie ?? "",
          },
          body: JSON.stringify(payload),
          signal: cartOffersAbortController.signal,
        }
      );
      
      const jsonResponse = await response.json();
      const signature = jsonResponse.data.signature;
      const requestId = window.breeze.generateShortUniqueId("cart");
      
      const processPayload = {
        requestId,
        service: "in.breeze.onecco",
        payload: { ...payload, signature },
      };

      if (!window.BlazeSDK || typeof window.BlazeSDK.process !== "function") {
        console.error("BlazeSDK is not initialized or process is not a function");
        return;
      }
      
      window.BlazeSDK.process(processPayload);
      
      // Track performance
      performanceMetrics.offerFetchTime = performance.now() - startTime;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("Cart offers request aborted");
      } else {
        console.error("Error fetching and rendering offers in cart:", error);
      }
    } finally {
      cartOffersRequestInProgress = false;
    }
  };

  /**
   * Fetch and render PDP offers with debouncing and optimization
   */
  const fetchAndRenderPDPOffers = async (reason = "") => {
    // Clear previous debounce timer
    if (pdpOffersDebounceTimer) {
      clearTimeout(pdpOffersDebounceTimer);
    }
    
    pdpOffersDebounceTimer = setTimeout(async () => {
      if (pdpOffersRequestInProgress) {
        console.log("PDP Offers request already in progress, skipping...");
        return;
      }
      
      pdpOffersRequestInProgress = true;
      const startTime = performance.now();

      // Abort previous request if any
      if (pdpOffersAbortController) {
        pdpOffersAbortController.abort();
      }
      pdpOffersAbortController = new AbortController();
      
      try {
        const cartItems = await window.cultstore.productData;
        const customerId = getCustomerIdFromLocalStorage();
        const transformedCartItems = [cartItems].map((item) => ({
          id: item.selectedVariant.id.toString(),
          type: item.productType.replace(/\s+/g, "_"),
          price: (item.selectedVariant.price / 100).toString(),
          quantity: 1,
        }));
        
        const cacheKey = `pdp-${customerId}-${transformedCartItems[0].id}`;
        
        // Check cache first
        const cachedOffers = getCachedOffer(cacheKey);
        if (cachedOffers) {
          console.log("Using cached PDP offers");
          window.breeze.offersCallback(cachedOffers);
          pdpOffersRequestInProgress = false;
          return;
        }
        
        const payload = {
          keyId: "10291",
          action: "listOffers",
          customerData: { id: customerId },
          cart: JSON.stringify({ items: transformedCartItems }),
        };
        
        const shopifyYCookie = getCookie("_shopify_y");
        const response = await fetch(
          "https://tesseract.cultstore.com/juspay/gen-offer-signature",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Device-ID": shopifyYCookie ?? "",
            },
            body: JSON.stringify(payload),
            signal: pdpOffersAbortController.signal,
          }
        );
        
        const jsonResponse = await response.json();
        const signature = jsonResponse.data.signature;
        const requestId = window.breeze.generateShortUniqueId("pdp");
        
        const processPayload = {
          requestId,
          service: "in.breeze.onecco",
          payload: { ...payload, signature },
        };
        
        if (!window.BlazeSDK || typeof window.BlazeSDK.process !== "function") {
          console.error("BlazeSDK is not initialized or process is not a function");
          return;
        }
        
        window.BlazeSDK.process(processPayload);
        
        // Track performance
        performanceMetrics.offerFetchTime = performance.now() - startTime;
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log("PDP offers request aborted");
        } else {
          console.error("Error fetching PDP offers:", error);
        }
      } finally {
        pdpOffersRequestInProgress = false;
      }
    }, 300);
  };

  // ============================================================================
  // HTML GENERATION
  // ============================================================================

  /**
   * Generate Terms & Conditions HTML
   */
  const generatePDPOfferItemTnCHTML = (offerTnC) => {
    const tncItems = offerTnC
      .replace(/<div class=['"]product-categories['"][^>]*>[\s\S]*?<\/div>/, '')
      .split("-")
      .filter((item) => item.trim() !== "" && item.trim() !== " ")
      .map((item) => `<li>${item.replace(/\n/g, "").trim()}</li>`)
      .join("");
    return tncItems;
  };

  /**
   * Generate individual offer card HTML
   */
  window.breeze.generateCartOfferItemHTML = function(offer, index, isPdp = false) {
    const isBestOffer = index === 0;
    const cartTotal = window.allOffers.cartData.cartTotal;
    const amountToUnlock = isPdp
      ? window.cultstore.productData.selectedVariant.price / 100
      : cartTotal;

    const finalOrderAmount = offer.orderBreakup.finalOrderAmount;
    const displayAmount =
      finalOrderAmount > 0
        ? `₹${Math.trunc(finalOrderAmount)}`
        : finalOrderAmount === 0
          ? "free"
          : "N/A";

    const appliedOffer = JSON.parse(localStorage.getItem("appliedOffer") || "null");
    const active = appliedOffer && appliedOffer.code === offer.offerCode;

    if (isPdp) {
      return `
        <discount-code-item>
          ${isBestOffer ? '<div class="cart-offer-callout-card-header">Best Offer - Apply at Checkout</div>' : ""}
          <div class="cart-offer-callout-card">
            <div class="cart-offer-callout-card-body">
              <div class="cart-offer-callout-inner-card-body">
                <div class="cart-offer-callout-card-body-header ${offer.status === "ELIGIBLE" ? "row-reverse" : "column-reverse"}">
                  <div><p>${offer.status === "ELIGIBLE" ? (offer.orderBreakup.finalOrderAmount === 0 ? "Get for <span>free</span> with coupon" : "Get for <span>₹" + Math.trunc(offer.orderBreakup.finalOrderAmount) + "</span> with coupon") : offer.offerDescription.description}</p></div>
                  <div class="offer-code">${offer.offerCode}</div>
                </div>
              </div>
            </div>
            <div class="unlock-offer hidden ${offer.offerRules.amount.minOrderAmount < amountToUnlock ? "hidden" : ""}">Add items worth ₹${Math.trunc(offer.offerRules.amount.minOrderAmount - amountToUnlock)} to unlock</div>
            <div class="cart-offer-callout-card-body-footer">
              <span class="cart-offer-callout-card-know-more-btn">Know More</span>
              <div class="cart-offer-callout-card-tc-container hidden">
                <h4>Terms and Conditions</h4>
                <ul class="cart-offer-callout-card-tc-pointer">
                  ${generatePDPOfferItemTnCHTML(offer.offerDescription.tnc || "N/A")}
                </ul>
              </div>
              <span class="cart-offer-callout-card-show-less-btn hidden">Show less</span>
            </div>
          </div>
        </discount-code-item>
      `;
    }

    return `
      <discount-code-item>
        ${isBestOffer ? '<div class="cart-offer-callout-card-header">Best Offer</div>' : ""}
        <div class="cart-offer-callout-card">
          <div class="cart-offer-callout-card-body">
            <div class="cart-offer-callout-inner-card-body">
              <div class="cart-offer-callout-card-body-header">
                <div data-offer-id="${offer.offerId}" data-offer-code="${offer.offerCode}" data-cart-total="${Math.trunc(offer.orderBreakup.finalOrderAmount)}" class="offer-code">${offer.offerCode}</div>
                <div class="${offer.offerRules.amount.minOrderAmount > cartTotal ? "" : "hidden"}"><p>${offer.offerDescription.description}</p></div>
                <div class="cart-offer-apply-container apply-btn ${offer.offerRules.amount.minOrderAmount > cartTotal ? "hidden" : ""}"><p style="margin: 0;">Get for <span data-discount-amount="${Math.trunc(offer.orderBreakup.discountAmount)}">${displayAmount}</span> with coupon</p><span class="apply-btn-text">${active ? "REMOVE" : "APPLY"}</span></div>
              </div>
            </div>
          </div>
          <div class="unlock-offer hidden ${offer.offerRules.amount.minOrderAmount < amountToUnlock ? "hidden" : ""}">Add items worth ₹${Math.trunc(offer.offerRules.amount.minOrderAmount - amountToUnlock)} to unlock</div>
          <div class="cart-offer-callout-card-body-footer">
            <span class="cart-offer-callout-card-know-more-btn">Know More</span>
            <div class="cart-offer-callout-card-tc-container hidden">
              <h4>Terms and Conditions</h4>
              <ul class="cart-offer-callout-card-tc-pointer">
                ${generatePDPOfferItemTnCHTML(offer.offerDescription.tnc || "N/A")}
              </ul>
            </div>
            <span class="cart-offer-callout-card-show-less-btn hidden">Show less</span>
          </div>
        </div>
      </discount-code-item>
    `;
  };

  /**
   * Generate complete cart offers HTML
   */
  window.BlazeSDK.generateCartOfferHTML = function(offers, ieOffers = []) {
    const offerItemHTML = offers
      .sort((a, b) => b.orderBreakup.offerAmount - a.orderBreakup.offerAmount)
      .map((offer, index) => window.breeze.generateCartOfferItemHTML(offer, index));
    
    const ieOffersItemHTML = ieOffers
      .filter((off) => off.offerRules.amount.minOrderAmount > window.allOffers.cartData.cartTotal)
      .sort((a, b) => {
        const cartTotal = window.allOffers.cartData.cartTotal;
        const diffA = a.offerRules.amount.minOrderAmount - cartTotal;
        const diffB = b.offerRules.amount.minOrderAmount - cartTotal;
        return diffA === diffB 
          ? b.orderBreakup.offerAmount - a.orderBreakup.offerAmount 
          : diffA - diffB;
      })
      .map((ieOffer, index) => window.breeze.generateCartOfferItemHTML(ieOffer, index + 1));

    const lockIcon = `<svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M4.52482 5.71433H11.4968L11.4976 4.88264C11.4976 2.95737 9.93688 1.39663 8.01161 1.39663L7.83763 1.4009C5.99319 1.49156 4.52561 3.01571 4.52561 4.88264L4.52482 5.71433ZM12.8945 5.75652V4.88249C12.8945 2.18597 10.7085 0 8.01202 0C5.31549 0 3.12953 2.18597 3.12953 4.88249V5.75275C1.53818 5.99659 0.319336 7.37145 0.319336 9.03098V14.6833C0.319336 16.5151 1.80425 18 3.63599 18H12.364C14.1958 18 15.6807 16.5151 15.6807 14.6833V9.03098C15.6807 7.3798 14.4741 6.01043 12.8945 5.75652ZM12.3637 7.11047H3.63565C2.57517 7.11047 1.71548 7.97016 1.71548 9.03064V14.683C1.71548 15.7435 2.57517 16.6032 3.63565 16.6032H12.3637C13.4242 16.6032 14.2839 15.7435 14.2839 14.683V9.03064C14.2839 7.97016 13.4242 7.11047 12.3637 7.11047ZM8.7102 13.078C9.24866 12.818 9.62013 12.2668 9.62013 11.6288C9.62013 10.7405 8.90004 10.0204 8.01177 10.0204C7.12349 10.0204 6.4034 10.7405 6.4034 11.6288C6.4034 12.2669 6.77505 12.8182 7.31371 13.0782V14.5457L7.31915 14.6333C7.36223 14.9776 7.65599 15.2439 8.01195 15.2439C8.39758 15.2439 8.7102 14.9313 8.7102 14.5457V13.078Z" fill="#393A3B"/>
    </svg>`;

    return `
      <extra-offers-container class="cart-offer-callout-card-container">
        ${!offerItemHTML[0] && ieOffersItemHTML[0] ? `
          <div class="cart-offer-callout-card-show-more-container-ineligible">
            <div class="ineligible-title">${lockIcon}<span>Unlock Offers</span></div>
        ` : ''}
        ${offerItemHTML[0] ?? ieOffersItemHTML[0] ?? ""}
        ${!offerItemHTML[0] && ieOffersItemHTML[0] ? `</div>` : ""}
        <div class="cart-offer-callout-card-show-more-btn ${offers.length > 1 || ieOffers.length > 0 ? '' : 'hidden'}">
          ${offers.length > 1 || ieOffers.length > 0 ? '<span class="line"></span>' : ""}
          ${offers.length > 1 || (offers.length === 0 && ieOffers.length > 1) || ieOffers.length > 0 
            ? `<span class="btn">More Offers (${offers.length + ieOffers.filter(off => off.offerRules.amount.minOrderAmount > window.allOffers.cartData.cartTotal).length - 1})<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="icon feather feather-chevron-down" aria-hidden="true" focusable="false" role="presentation"><path d="m6 9 6 6 6-6"/></svg></span>` 
            : ""}
        </div>
        <div class="cart-offer-callout-card-show-more-container hidden">
          <div class="cart-offer-callout-card-show-more-container-eligible">
            ${offers.length > 1 ? offerItemHTML.slice(1).join("") : ""}
          </div>
          ${ieOffersItemHTML.length > 1 && offers.length === 0 ? `
            <div class="cart-offer-callout-card-show-more-container-ineligible">
              <div class="ineligible-title">${lockIcon}<span>Unlock Offers</span></div>
              ${ieOffersItemHTML.slice(1).join("")}
            </div>
          ` : ""}
          ${ieOffersItemHTML.length > 0 && offers.length !== 0 ? `
            <div class="cart-offer-callout-card-show-more-container-ineligible">
              <div class="ineligible-title">${lockIcon}<span>Unlock Offers</span></div>
              ${ieOffersItemHTML.join("")}
            </div>
          ` : ""}
        </div>
      </extra-offers-container>
    `;
  };

  /**
   * Generate PDP offer HTML
   */
  window.BlazeSDK.generatePDPOfferHTML = function(offer, index, isPdp = false) {
    const offerItemHTML = window.breeze.generateCartOfferItemHTML(offer, index, isPdp);
    return `<div class="modal-offer-item">${offerItemHTML}</div>`;
  };

  // ============================================================================
  // RESPONSE PROCESSING
  // ============================================================================

  /**
   * Process response from Breeze API
   */
  window.breeze.processResponse = async function(offersResponse) {
    const response = JSON.parse(offersResponse);
    const { requestId, payload } = response;
    const { action, event } = payload;
    
    if (action === "listOffers") {
      window.breeze.offersCallback(response);
    } else if (action === "startCheckout") {
      const customEvent = new CustomEvent("CHECKOUT_PROCESS_INTERNAL", {
        detail: event === "Purchase" 
          ? { requestId, event, data: payload.data }
          : ["OfferApplied", "OfferRemoved", "PayNow"].includes(event)
            ? { requestId, event, data: response }
            : { requestId, event },
      });
      document.dispatchEvent(customEvent);
    }
  };

  /**
   * Process offers callback
   */
  window.breeze.offersCallback = function(offers) {
    const offersData = (offers.payload.offers || [])
      .filter((offer) => offer?.status === "ELIGIBLE" && offer.offerCode !== "EMIONCARD");

    const inEligibleOffers = (offers.payload.offers || [])
      .filter((offer) => offer?.status !== "ELIGIBLE" && offer.offerCode !== "EMIONCARD");

    const requestId = offers.requestId || "";
    
    // Cache the actual response for future use
    if (requestId) {
      const customerId = getCustomerIdFromLocalStorage();
      if (requestId.includes("cart")) {
        const cacheKey = `cart-${customerId}-${requestId}`;
        setCachedOffer(cacheKey, offers);
      } else if (requestId.includes("pdp")) {
        const cacheKey = `pdp-${customerId}-${requestId}`;
        setCachedOffer(cacheKey, offers);
      }
    }
    
    if (requestId.includes("cart")) {
      // Cart offers
      try {
        const renderStart = performance.now();
        
        window.allOffers.cartOffers = offersData;
        window.allOffers.inEligibleOffers.cart = inEligibleOffers;
        const offerContainer = document.querySelector(".cart-drawer__offers");
        if (offerContainer) {
          // Use requestAnimationFrame for smoother rendering
          requestAnimationFrame(() => {
            offerContainer.innerHTML = window.BlazeSDK.generateCartOfferHTML(
              window.allOffers.cartOffers,
              window.allOffers.inEligibleOffers.cart
            );
            offerContainer
              .querySelectorAll(".cart-offer-callout-card-show-more-container-ineligible .unlock-offer")
              .forEach((unlockOffer) => unlockOffer.classList.remove("hidden"));
            
            performanceMetrics.renderTime = performance.now() - renderStart;
            console.log(`Cart offers rendered in ${performanceMetrics.renderTime.toFixed(2)}ms`);
          });
        }
      } catch (error) {
        console.error("Error rendering cart offers:", error);
      }
    } else if (requestId.includes("pdp")) {
      // PDP offers
      window.allOffers.pdpOffers = offersData;
      window.allOffers.inEligibleOffers.pdpOffers = inEligibleOffers;
      
      const offerParentContainer = document.querySelector(".product-discount-container");
      const offerContainer = document.querySelector(".product-discount-container-inner");
      const additionalOffersContainerParent = document.querySelector(".product-discount-additional-container");
      const additionalOffersContainer = document.querySelector(".product-discount-additional-container .product-discount-additional");
      const offerModalContainer = document.querySelector(".offer-modal-body");
      
      // Only proceed if we're on a product page
      if (!offerContainer && !offerModalContainer) {
        console.log("Not on PDP, skipping PDP offer rendering");
        return;
      }
      
      try {
        if (offersData.length === 0 && inEligibleOffers.length > 0) {
          additionalOffersContainerParent?.classList.add("grayed-out");
          additionalOffersContainer?.classList.add("aligncenter");
          if (additionalOffersContainer) {
            additionalOffersContainer.innerHTML = `
              <svg width="15" height="15" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.52482 5.71433H11.4968L11.4976 4.88264C11.4976 2.95737 9.93688 1.39663 8.01161 1.39663L7.83763 1.4009C5.99319 1.49156 4.52561 3.01571 4.52561 4.88264L4.52482 5.71433ZM12.8945 5.75652V4.88249C12.8945 2.18597 10.7085 0 8.01202 0C5.31549 0 3.12953 2.18597 3.12953 4.88249V5.75275C1.53818 5.99659 0.319336 7.37145 0.319336 9.03098V14.6833C0.319336 16.5151 1.80425 18 3.63599 18H12.364C14.1958 18 15.6807 16.5151 15.6807 14.6833V9.03098C15.6807 7.3798 14.4741 6.01043 12.8945 5.75652ZM12.3637 7.11047H3.63565C2.57517 7.11047 1.71548 7.97016 1.71548 9.03064V14.683C1.71548 15.7435 2.57517 16.6032 3.63565 16.6032H12.3637C13.4242 16.6032 14.2839 15.7435 14.2839 14.683V9.03064C14.2839 7.97016 13.4242 7.11047 12.3637 7.11047ZM8.7102 13.078C9.24866 12.818 9.62013 12.2668 9.62013 11.6288C9.62013 10.7405 8.90004 10.0204 8.01177 10.0204C7.12349 10.0204 6.4034 10.7405 6.4034 11.6288C6.4034 12.2669 6.77505 12.8182 7.31371 13.0782V14.5457L7.31915 14.6333C7.36223 14.9776 7.65599 15.2439 8.01195 15.2439C8.39758 15.2439 8.7102 14.9313 8.7102 14.5457V13.078Z" fill="#393A3B"/>
              </svg>
              Unlock Offers`;
          }
        }
        
        // Use document fragment for batch DOM updates
        requestAnimationFrame(() => {
          const renderStart = performance.now();
          
          // Only clear and re-render if we have offers to show
          if (window.allOffers.pdpOffers.length > 0) {
            // Clear existing offers before rendering new ones
            if (offerContainer) {
              // Remove only dynamically added offer items, keep static content
              offerContainer.querySelectorAll('.offer-item').forEach(el => el.remove());
            }
            if (offerModalContainer) {
              offerModalContainer.querySelectorAll('.modal-offer-item').forEach(el => el.remove());
            }
            
            const fragment = document.createDocumentFragment();
            const modalFragment = document.createDocumentFragment();
            
            window.allOffers.pdpOffers
              .sort((a, b) => b.orderBreakup.offerAmount - a.orderBreakup.offerAmount)
              .forEach((offer, index) => {
                const offerElement = document.createElement("div");
                offerElement.classList.add("offer-item");
                offerElement.setAttribute("data-offer-id", offer.offerId);
                offerElement.innerHTML = window.BlazeSDK.generatePDPOfferHTML(offer, index + 1, true);

                if (index === 0 && offerContainer) {
                  fragment.appendChild(offerElement);
                }

                if (offerModalContainer) {
                  const modalOfferElement = document.createElement("div");
                  modalOfferElement.classList.add("modal-offer-item");
                  modalOfferElement.setAttribute("data-offer-id", offer.offerId);
                  modalOfferElement.innerHTML = window.BlazeSDK.generatePDPOfferHTML(offer, index + 1);
                  modalFragment.appendChild(modalOfferElement);
                }
              });
            
            // Batch append to DOM
            if (offerContainer && fragment.hasChildNodes()) {
              offerContainer.appendChild(fragment);
            }
            if (offerModalContainer && modalFragment.hasChildNodes()) {
              offerModalContainer.appendChild(modalFragment);
            }
          }
          
          // Show/hide parent container after rendering
          if ((offersData.length > 0 || inEligibleOffers.length > 0) && offerParentContainer) {
            offerParentContainer.classList.remove("hidden");
          }
          const footerElement = offerParentContainer?.querySelector(".cart-offer-callout-card-body-footer");
          if (footerElement) {
            footerElement.style.display = "none";
          }
          
          performanceMetrics.renderTime = performance.now() - renderStart;
          console.log(`PDP offers rendered in ${performanceMetrics.renderTime.toFixed(2)}ms`);
        });
      } catch (error) {
        console.error("Error rendering PDP offers:", error);
      }
    }
  };

  // ============================================================================
  // CUSTOM ELEMENTS
  // ============================================================================

  /**
   * Extra Offers Container - Expandable offers list with event delegation
   */
  class ExtraOffersContainer extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      // Use event delegation instead of individual listeners
      this.addEventListener("click", this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
    }

    handleClick(event) {
      const showMoreBtn = event.target.closest(".cart-offer-callout-card-show-more-btn");
      if (showMoreBtn) {
        const container = this.querySelector(".cart-offer-callout-card-show-more-container");
        if (container?.classList.contains("hidden")) {
          this.showMoreOffers();
        } else {
          this.hideMoreOffers();
        }
      }
    }

    showMoreOffers() {
      this.querySelector(".cart-offer-callout-card-show-more-container")?.classList.remove("hidden");
      const svg = this.querySelector(".cart-offer-callout-card-show-more-btn svg");
      if (svg) svg.style.transform = "rotate(180deg)";
    }

    hideMoreOffers() {
      this.querySelector(".cart-offer-callout-card-show-more-container")?.classList.add("hidden");
      const svg = this.querySelector(".cart-offer-callout-card-show-more-btn svg");
      if (svg) svg.style.transform = "rotate(0deg)";
    }
  }

  /**
   * Discount Code Item - Individual offer card with event delegation
   */
  class DiscountCodeItem extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.initialized = false;
    }

    connectedCallback() {
      // Lazy initialization on first interaction or when visible
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.initialized) {
              this.initialize();
              observer.disconnect();
            }
          });
        }, { threshold: 0.1 });
        
        observer.observe(this);
      } else {
        // Fallback for browsers without IntersectionObserver
        this.initialize();
      }

      // Use event delegation
      this.addEventListener("click", this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
    }

    initialize() {
      if (this.initialized) return;
      this.initialized = true;

      // Check for applied offer and update button text
      const appliedOffer = JSON.parse(localStorage.getItem("appliedOffer") || "null");
      const offerCodeEl = this.querySelector(".offer-code");
      if (appliedOffer && offerCodeEl && appliedOffer.code === offerCodeEl.getAttribute("data-offer-code")) {
        const applyBtnText = this.querySelector(".apply-btn-text");
        if (applyBtnText) applyBtnText.innerHTML = "REMOVE";
      }

      this.waitForCartOffers(appliedOffer);
    }

    handleClick(event) {
      const knowMoreBtn = event.target.closest(".cart-offer-callout-card-know-more-btn");
      const showLessBtn = event.target.closest(".cart-offer-callout-card-show-less-btn");
      const applyBtn = event.target.closest(".cart-offer-apply-container.apply-btn");

      if (knowMoreBtn) {
        this.showTnC();
      } else if (showLessBtn) {
        this.hideTnC();
      } else if (applyBtn) {
        const offer = {
          id: this.querySelector(".offer-code")?.getAttribute("data-offer-id"),
          code: this.querySelector(".offer-code")?.getAttribute("data-offer-code"),
          cartTotal: this.querySelector(".offer-code")?.getAttribute("data-cart-total"),
        };
        this.applyOffer(offer, this.querySelector(".apply-btn-text"));
      }
    }

    waitForCartOffers(appliedOffer) {
      let retryCount = 0;
      const maxRetries = 5;

      const check = () => {
        if (window.allOffers.cartOffers && window.allOffers.cartOffers.length > 0) {
          if (appliedOffer) {
            const offer = window.allOffers.cartOffers.find((o) => o.offerCode === appliedOffer.code);
            if (offer && offer.status === "ELIGIBLE") {
              localStorage.setItem("appliedOffer", JSON.stringify({
                id: offer.offerId,
                code: offer.offerCode,
                cartTotal: offer.orderBreakup.finalOrderAmount,
              }));
              const subtotalEl = document.querySelector(".cart-drawer__footer .subtotal .theme-money");
              if (subtotalEl) {
                subtotalEl.innerHTML = formatIndianRupees(Math.trunc(offer.orderBreakup.finalOrderAmount));
              }
            } else {
              localStorage.removeItem("appliedOffer");
              document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
            }
          }
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(check, 100);
        }
      };

      check();
    }

    applyOffer(offer, el) {
      const appliedOffer = JSON.parse(localStorage.getItem("appliedOffer") || "null");
      if (appliedOffer && appliedOffer.code === offer.code) {
        localStorage.removeItem("appliedOffer");
        document.querySelectorAll(".apply-btn-text").forEach((ele) => (ele.innerHTML = "APPLY"));
        if (el) el.innerHTML = "APPLY";
        document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      } else {
        localStorage.setItem("appliedOffer", JSON.stringify(offer));
        document.querySelectorAll(".apply-btn-text").forEach((ele) => (ele.innerHTML = "APPLY"));
        if (el) el.innerHTML = "REMOVE";
        const subtotalEl = document.querySelector(".cart-drawer__footer .subtotal .theme-money");
        if (subtotalEl) {
          subtotalEl.innerHTML = formatIndianRupees(Math.trunc(offer.cartTotal));
        }
        this.showAppliedCoupon();
      }
    }

    showAppliedCoupon() {
      const offerCode = this.querySelector(".cart-offer-callout-card-body-header .offer-code")?.innerText;
      const discountAmount = this.querySelector(".cart-offer-apply-container p span")?.getAttribute("data-discount-amount");
      const cartOfferApplied = document.querySelector("cart-offer-applied");
      if (cartOfferApplied && offerCode && discountAmount) {
        cartOfferApplied.show(`₹${discountAmount} saved`, offerCode);
      }
    }

    showTnC() {
      this.querySelector(".cart-offer-callout-card-tc-container")?.classList.remove("hidden");
      this.querySelector(".cart-offer-callout-card-show-less-btn")?.classList.remove("hidden");
      this.querySelector(".cart-offer-callout-card-know-more-btn")?.classList.add("hidden");
    }

    hideTnC() {
      this.querySelector(".cart-offer-callout-card-tc-container")?.classList.add("hidden");
      this.querySelector(".cart-offer-callout-card-show-less-btn")?.classList.add("hidden");
      this.querySelector(".cart-offer-callout-card-know-more-btn")?.classList.remove("hidden");
    }
  }

  /**
   * Cart Offer Applied - Success notification
   */
  class CartOfferApplied extends HTMLElement {
    constructor() {
      super();
    }

    show(title, coupon, duration = 3000) {
      const titleEl = this.querySelector(".cart-offer-applied__title");
      const descEl = this.querySelector(".cart-offer-applied__description span");
      if (titleEl) titleEl.innerText = title;
      if (descEl) descEl.innerText = coupon;
      this.style.display = "flex";
      setTimeout(() => this.close(), duration);
    }

    close() {
      this.style.display = "none";
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize BlazeSDK with singleton pattern
   */
  function initializeBlazeSDK() {
    // Return existing promise if initialization already started
    if (blazeSDKInitPromise) {
      return blazeSDKInitPromise;
    }

    // Create new initialization promise
    blazeSDKInitPromise = new Promise((resolve, reject) => {
      if (blazeSDKInitialized) {
        resolve(true);
        return;
      }

      if (window.BlazeSDK && typeof window.BlazeSDK.initiate === "function") {
        try {
          blazeSDKInitialized = true;
          sdkInitStartTime = performance.now();
          
          window.BlazeSDK.initiate(
            {
              merchantId: "curefit",
              shopUrl: "https://cult-demo.myshopify.com/",
              environment: "production",
              platform: "Shopify",
            },
            window.breeze.processResponse
          );
          
          performanceMetrics.sdkLoadTime = performance.now() - sdkInitStartTime;
          console.log(`BlazeSDK initialized in ${performanceMetrics.sdkLoadTime.toFixed(2)}ms`);
          
          // Fetch offers after SDK is ready
          if (window.location.pathname.includes("/products/")) {
            fetchAndRenderPDPOffers("PDP Visited");
          }
          fetchAndRenderOffersInCart();
          
          resolve(true);
        } catch (error) {
          console.error("Error initializing BlazeSDK:", error);
          reject(error);
        }
      } else {
        reject(new Error("BlazeSDK not available"));
      }
    });

    return blazeSDKInitPromise;
  }

  /**
   * Wait for BlazeSDK to load with retry logic
   */
  const waitForBlazeSDK = (() => {
    const startTime = Date.now();
    let errorLogged = false;
    let retryCount = 0;
    const maxRetries = 20; // 20 * 100ms = 2 seconds

    return function check() {
      if (blazeSDKInitialized) {
        return;
      }

      if (window.BlazeSDK && typeof window.BlazeSDK.initiate === "function") {
        initializeBlazeSDK();
      } else {
        const elapsed = Date.now() - startTime;
        retryCount++;
        
        if (retryCount >= maxRetries && !errorLogged) {
          errorLogged = true;
          console.error(`Breeze SDK did not load within ${elapsed}ms after ${maxRetries} attempts.`);
          
          // Report to Sentry if available
          if (window.CultStoreSentry) {
            window.CultStoreSentry.captureMessage('BlazeSDK load timeout', {
              level: 'error',
              extra: {
                elapsed,
                retries: retryCount,
                userAgent: navigator.userAgent,
              },
            });
          }
        } else if (!blazeSDKInitialized && retryCount < maxRetries) {
          setTimeout(check, 100);
        }
      }
    };
  })();

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Register custom elements
    customElements.define("extra-offers-container", ExtraOffersContainer);
    customElements.define("discount-code-item", DiscountCodeItem);
    customElements.define("cart-offer-applied", CartOfferApplied);

    // Wait for and initialize BlazeSDK
    waitForBlazeSDK();

    // Listen for cart changes
    window.addEventListener("cart_changed", (e) => {
      window.allOffers.cartData.cartTotal = e.detail.cartTotal;
      fetchAndRenderOffersInCart(e.detail.cart);
      if (window.location.pathname.includes("/products/")) {
        fetchAndRenderPDPOffers("Cart changed");
      }
    });
  }

  // ============================================================================
  // PUBLIC API EXPORTS
  // ============================================================================

  /**
   * Expose public APIs for external use
   */
  window.breeze = window.breeze || {};
  
  // Extend existing breeze namespace with new utilities
  Object.assign(window.breeze, {
    // Utility functions
    getCookie: getCookie,
    getCustomerIdFromLocalStorage: getCustomerIdFromLocalStorage,
    formatIndianRupees: formatIndianRupees,
    
    // SDK Management
    waitForSDK: waitForBreezeSDK,
    isSDKReady: () => {
      return typeof window.BlazeSDK !== 'undefined' && 
             typeof window.BlazeSDK.process === 'function';
    },
    
    // Session Management
    fetchSession: fetchCustomerSession,
    
    // Checkout Management
    startCheckout: startBreezeCheckout,
    
    // Offers Management
    fetchCartOffers: fetchAndRenderOffersInCart,
    fetchPDPOffers: fetchAndRenderPDPOffers,
    
    // State Access
    getOffers: () => window.allOffers,
    
    // Already exposed from original code:
    // - onCheckoutButtonClick
    // - generateShortUniqueId
    // - processResponse
    // - offersCallback
    // - generateCartOfferItemHTML
  });

})();
